import { CHESS_SIDE, CHESS_START_POSITION } from "../constants";
import { checkGameResult, generateResultString } from "../utils/result-helper";
export class PgnMove {
    id;
    number;
    color;
    san;
    from;
    to;
    promotion;
    fen;
    pgn;
    variationDepth = 0;
    parentMove;
    childMoves;
    previousMoveInline;
    nextMove;
    comment;
    nag;
    _isPuzzleMove = false;
    _userScore;
    _gameResult;
    constructor(san, chess, previousMove, parentMove, initialFen = CHESS_START_POSITION) {
        // validate parentMove
        if (previousMove && previousMove.nextMove) {
            if (!parentMove) {
                parentMove = previousMove.nextMove;
            }
            else {
                if (parentMove.id !== previousMove.nextMove.id) {
                    console.log({ previousMove, parentMove });
                    throw new Error(`${san} Invalid parent move ${parentMove.id}, should be ${previousMove.nextMove.id}`);
                }
            }
        }
        const prevPgn = previousMove?.pgn ?? parentMove?.previousMove?.pgn ?? null;
        if (prevPgn) {
            chess.load_pgn(prevPgn.replace(/\n\n\d+\.\s/, "\n1. "));
        }
        else {
            chess.load(initialFen);
        }
        const prevFen = chess.fen();
        // Get move details from chess.js
        const moveResult = chess.move(san, { sloppy: true });
        if (!moveResult) {
            console.error(`Invalid move: ${san} from ${chess.pgn()}`);
            return;
        }
        const gameResult = checkGameResult(chess);
        if (gameResult) {
            this._gameResult = gameResult;
        }
        // Check for duplicate moves
        if (parentMove) {
            // Check if same as parent move
            if (parentMove.san === moveResult.san) {
                throw new Error(`Duplicate move: ${san} is the same as parent move`);
            }
            // Check siblings (other variations of the same parent)
            for (const sibling of parentMove.childMoves || []) {
                if (sibling.san === moveResult.san) {
                    throw new Error(`Duplicate move: ${san} already exists as a variation`);
                }
            }
        }
        // Basic properties setup
        this.number = parseInt(prevFen.split(" ")[5]) || 1;
        this.color = moveResult.color;
        this.san = moveResult.san;
        this.from = moveResult.from;
        this.to = moveResult.to;
        this.promotion = moveResult.promotion;
        this.fen = chess.fen();
        this.pgn = chess.pgn();
        this.variationDepth = 0;
        // Link previous move
        if (previousMove && !parentMove) {
            this.number =
                this.color === "w" ? previousMove.number + 1 : previousMove.number;
            this.variationDepth = previousMove.variationDepth;
            this.previousMoveInline = previousMove;
            previousMove.nextMove = this;
        }
        if (parentMove) {
            this.number = parentMove.number;
            this.variationDepth = parentMove.variationDepth + 1;
            this.parentMove = parentMove;
            if (parentMove.childMoves === undefined) {
                parentMove.childMoves = [];
            }
            parentMove.childMoves.push(this);
        }
        // Generate ID using the specified format
        const cleanSan = this.san.replace(/[#+]/g, "");
        const prevFenHash = this.hashFen(prevFen);
        this.id = `${this.number}-${this.variationDepth}-${this.color}-${cleanSan}-${prevFenHash}`;
    }
    hashFen(fen) {
        let hash = 0;
        for (let i = 0; i < fen.length; i++) {
            const char = fen.charCodeAt(i);
            hash = (hash << 5) - hash + char;
            hash = hash & hash;
        }
        return Math.abs(hash).toString(16).slice(-4);
    }
    get previousMove() {
        return this.previousMoveInline || this.parentMove?.previousMove || null;
    }
    get isVariationBegin() {
        return this.variationDepth > 0 && !this.previousMoveInline;
    }
    get isVariationEnd() {
        return this.variationDepth > 0 && !this.nextMove;
    }
    get firstMoveInLine() {
        if (!this.previousMoveInline) {
            return this;
        }
        return this.previousMoveInline.firstMoveInLine;
    }
    get lastMoveInLine() {
        if (!this.nextMove) {
            return this;
        }
        return this.nextMove.lastMoveInLine;
    }
    /**
     * Returns a string representation of the move following PGN standards.
     * - NAGs are represented in their standard notation (!, ?, !!, etc.)
     * - Comments are enclosed in curly braces {}
     * - User move scores are included as a special comment {[%mus score]}
     * @returns {string} PGN-compliant string representation of the move
     */
    toString(options = {}) {
        const { includeUserScore = true } = options;
        let result = this.san;
        // Add NAG if present
        if (this.nag) {
            result += ` ${this.nag}`;
        }
        // Add user score if enabled
        if (includeUserScore && this._userScore != null) {
            result += ` {[%mus ${this._userScore}]}`;
        }
        // Add comment if present
        if (this.comment) {
            result += ` {${this.comment}}`;
        }
        return result;
    }
    isInlineAndBefore(moveId) {
        let nextMove = this.nextMove;
        while (nextMove) {
            if (nextMove.id === moveId) {
                return true;
            }
            nextMove = nextMove.nextMove;
        }
        return false;
    }
    isInlineAndAfter(moveId) {
        let previousMove = this.previousMove;
        while (previousMove) {
            if (previousMove.id === moveId) {
                return true;
            }
            previousMove = previousMove.previousMove;
        }
        return false;
    }
    isInline(moveId) {
        return this.isInlineAndBefore(moveId) || this.isInlineAndAfter(moveId);
    }
    findMoveById(moveId) {
        if (this.id === moveId) {
            return this;
        }
        if (this.nextMove) {
            const foundMove = this.nextMove.findMoveById(moveId);
            if (foundMove) {
                return foundMove;
            }
        }
        for (const childMove of this.childMoves || []) {
            const foundMove = childMove.findMoveById(moveId);
            if (foundMove) {
                return foundMove;
            }
        }
        return null;
    }
    rows(options = {}) {
        const { includeVariations = true } = options;
        const self = this;
        let move = self;
        const moveRows = [];
        let currentMoveNumber = -1;
        while (move) {
            if (move.number !== currentMoveNumber ||
                move.variationDepth !== moveRows[moveRows.length - 1].variationDepth) {
                // new full move
                moveRows.push({
                    number: move.number,
                    variationDepth: move.variationDepth,
                    white: null,
                    black: null,
                });
                currentMoveNumber = move.number;
            }
            const currentRow = moveRows[moveRows.length - 1];
            if (move.color === "w") {
                if (currentRow.white) {
                    throw new Error(`Duplicate move number ${move.number}: ${currentRow.white.san} vs ${move.san}`);
                }
                currentRow.white = move;
            }
            else {
                if (currentRow.black) {
                    throw new Error(`Duplicate move number ${move.number}: ${currentRow.black.san} vs ${move.san}`);
                }
                currentRow.black = move;
            }
            if (includeVariations) {
                for (const childMove of move.childMoves || []) {
                    moveRows.push(...childMove.rows({ includeVariations }));
                }
            }
            move = move.nextMove;
        }
        return moveRows;
    }
    get isPuzzleMove() {
        return this._isPuzzleMove;
    }
    setIsPuzzleMove(isPuzzle) {
        this._isPuzzleMove = isPuzzle;
    }
    updateComment(comment) {
        this.comment = comment;
    }
    updateNag(nag) {
        this.nag = nag;
    }
    /**
     * Determines if this move is a "question move" that the player needs to solve in a puzzle.
     *
     * A move is considered a question if:
     * 1. It is marked as a puzzle move (isPuzzleMove = true)
     * 2. It is on the main line (variationDepth = 0)
     * 3. Either:
     *    - It is the first puzzle move in the sequence, OR
     *    - It has the same color as the first puzzle move
     *
     * @returns {boolean} True if this move is a question move, false otherwise
     */
    get isQuestion() {
        if (!this.isPuzzleMove || this.variationDepth > 0)
            return false;
        // Find the first puzzle move in the sequence
        const self = this;
        let firstPuzzleMove = self;
        while (firstPuzzleMove.previousMove?.isPuzzleMove) {
            firstPuzzleMove = firstPuzzleMove.previousMove;
        }
        return firstPuzzleMove.color === this.color;
    }
    // Returns a visual mask for hidden puzzle moves:
    // - '???' for moves that need to be solved
    // - '---' for opponent's moves in the puzzle sequence
    get mask() {
        if (this.isPuzzleMove && this._userScore == null) {
            return this.isQuestion ? "???" : "---";
        }
        return null;
    }
    get displayColor() {
        if (this._userScore === -1) {
            return "red"; // Wrong puzzle move
        }
        if (this.isPuzzleMove) {
            if (this._userScore === 1) {
                return "green"; // Correct puzzle move
            }
            if (this._userScore === 0) {
                return "blue"; // Revealed puzzle move
            }
        }
        if (this._userScore != null) {
            return "purple"; // Normal user move
        }
        return null; // part of scenario moves
    }
    get isCheckmate() {
        return this.san.toString().includes("#");
    }
    get userScore() {
        return this._userScore;
    }
    /**
     * Get the game result associated with this move
     * @returns The game result or null if the game is not over
     */
    get gameResult() {
        return this._gameResult;
    }
    /**
     * Get a human-readable result string
     * @returns A human-readable result string or null if the game is not over
     */
    get resultString() {
        if (!this._gameResult) {
            return null;
        }
        // Determine the color of the last move
        const lastMoveColor = this.color === "w" ? CHESS_SIDE.WHITE : CHESS_SIDE.BLACK;
        return generateResultString(this._gameResult, lastMoveColor);
    }
    loadUserScore(score) {
        this._userScore = score;
    }
    findMatchedPuzzleMove(san) {
        // First check this move
        if (this.isPuzzleMove && this._userScore == null && this.san === san) {
            return this;
        }
        // Then check child moves (variations)
        const matchedMove = (this.childMoves || []).find((m) => m.isPuzzleMove && m._userScore == null && m.san === san);
        return matchedMove || null;
    }
    recordRevealed() {
        if (this._userScore != null) {
            return;
        }
        if (!this.isPuzzleMove) {
            return;
        }
        this._userScore = 0;
    }
    recordUserMove() {
        if (this._userScore != null) {
            return;
        }
        if (this.isQuestion || this.isCheckmate) {
            // record a move as correct if it's a question move or a checkmate
            this._userScore = 1;
            return;
        }
        if (this.parentMove?.isQuestion) {
            // record an Incorrect Answer
            this._userScore = -1;
            return;
        }
        if (this.isPuzzleMove) {
            // Reveal a Puzzle Move
            this._userScore = 0;
            return;
        }
        // Record extra user move
        this._userScore = 0;
    }
}
